import { changeTheme } from "./theme-transition.js";

// The page's theme: the theme switch's saved choice, or else the system's light or dark
// setting. The head script applies it before first paint and keeps following the system
// setting until you pick one. This module holds no state, because both head scripts bundle
// their own copy of it.

/** The page theme. The head script sets `data-theme`, and the switch emits changes. */
export type Theme = "light" | "dark";

const THEME_KEY = "jrsm-theme";

const systemDark = (): MediaQueryList => matchMedia("(prefers-color-scheme: dark)");

/** The theme switch's saved choice, or null if there isn't one or storage is blocked. */
export function savedTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null;
  }
}

/** Save the theme switch's choice. If storage fails, the page still changes. */
export function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // No storage, so it won't stick, but the current page still changes.
  }
}

/** The system's light or dark setting. */
export function systemTheme(): Theme {
  return systemDark().matches ? "dark" : "light";
}

/** The theme the page is showing right now (`data-theme` on the root). */
export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

/** Calls `listener` after every theme change, whatever caused it: the switch, or the system
    setting while no choice is saved. It watches the state itself, not the switch's request
    (the `jrsm-theme` event), just like `onMotionChange` does for motion. */
export function onThemeChange(
  listener: (theme: Theme) => void,
  options: { signal?: AbortSignal } = {},
): void {
  const observer = new MutationObserver(() => listener(currentTheme()));
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  options.signal?.addEventListener("abort", () => observer.disconnect(), { once: true });
}

/** Show `theme`: sets the root's `data-theme` and `color-scheme`, and the browser bar's color.
    The bar has a tag for each system setting, and both get the theme's color. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    if (!(meta instanceof HTMLMetaElement)) continue;
    const color = meta.dataset[theme];
    if (color) meta.content = color;
  }
}

/** Until you pick a theme, changing the system setting changes the page too, with the same
    crossfade as the switch. */
export function followSystemTheme(): void {
  systemDark().addEventListener("change", () => {
    if (savedTheme() !== null) return;
    const theme = systemTheme();
    if (document.documentElement.dataset.theme !== theme) void changeTheme(() => applyTheme(theme));
  });
}
